import { supabase } from '@/lib/supabase';

export interface DispositionConfig {
  id: string;
  pipeline_type: string;
  group_name: string;
  group_order: number;
  code: string;
  display_name: string;
  item_order: number;
  requires_notes: boolean;
  requires_date: boolean;
  is_active: boolean;
  is_system: boolean;
  tenant_id?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectDisposition {
  disposition: string;
  disposition_notes?: string;
  disposition_date?: string;
  requires_follow_up: boolean;
  follow_up_date?: string;
  updated_at: string;
  updated_by?: string;
}

export interface DispositionHistory {
  id: string;
  project_id: string;
  previous_disposition?: string;
  new_disposition: string;
  notes?: string;
  scheduled_date?: string;
  changed_by: string;
  changed_at: string;
}

// Fetch project with disposition (LEFT JOIN)
export async function getProjectWithDisposition(projectId: string) {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      *,
      project_dispositions(
        disposition,
        disposition_notes,
        disposition_date,
        requires_follow_up,
        follow_up_date,
        updated_at
      )
    `)
    .eq('id', projectId)
    .single();

  if (error) throw error;
  return data;
}

// Update or create disposition (UPSERT)
export async function updateProjectDisposition(
  projectId: string,
  disposition: string,
  notes?: string,
  date?: string,
  requiresFollowUp?: boolean,
  followUpDate?: string
) {
  const { data: user } = await supabase.auth.getUser();
  const userId = user?.user?.id;

  // First check if user can access this project
  // This helps provide better error messages than the generic 403
  const { data: projectCheck, error: projectCheckError } = await supabase
    .from('projects')
    .select('id, tenant_id')
    .eq('id', projectId)
    .single();

  if (projectCheckError || !projectCheck) {
    console.error('Project access check failed:', projectCheckError);
    throw new Error('Unable to access this project. It may not exist or you may not have permission.');
  }

  try {
    const { data, error } = await supabase
      .from('project_dispositions')
      .upsert({
        project_id: projectId,
        disposition,
        disposition_notes: notes,
        disposition_date: date,
        requires_follow_up: requiresFollowUp || false,
        follow_up_date: followUpDate,
        updated_at: new Date().toISOString(),
        updated_by: userId
      }, {
        onConflict: 'project_id'
      })
      .select()
      .single();

    if (error) {
      console.error('Disposition update error:', error);
      // Check if it's a RLS policy violation
      if (error.code === '42501' || (error as any).status === 403) {
        throw new Error('You do not have permission to update dispositions for this project. Please check with your administrator.');
      }
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Failed to update disposition:', err);
    throw err;
  }
}

// Get disposition history
export async function getDispositionHistory(projectId: string) {
  const { data, error } = await supabase
    .from('project_disposition_history')
    .select(`
      *,
      changed_by_user:auth.users(id, email, raw_user_meta_data)
    `)
    .eq('project_id', projectId)
    .order('changed_at', { ascending: false });

  if (error) throw error;
  return data;
}

// Get available disposition options by pipeline type
export async function getDispositionConfigs(pipelineType: string, tenantId?: string) {
  let query = supabase
    .from('project_disposition_configs')
    .select('*')
    .eq('pipeline_type', pipelineType)
    .eq('is_active', true)
    .order('group_order')
    .order('item_order');

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  } else {
    query = query.is('tenant_id', null);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

// Get all projects with dispositions for a pipeline
export async function getProjectsWithDispositions(pipelineId: string) {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      *,
      project_dispositions(
        disposition,
        disposition_notes,
        disposition_date,
        requires_follow_up,
        follow_up_date
      )
    `)
    .eq('pipeline_id', pipelineId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// Helper function to get pipeline type from project
export async function getPipelineTypeFromProject(projectId: string): Promise<string> {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      pipeline_id,
      tenant_id,
      pipeline:pipelines(
        id,
        pipeline_type
      )
    `)
    .eq('id', projectId)
    .single();

  if (error) throw error;

  // Type assertion for nested relation
  const pipeline = (data as any)?.pipeline;

  if (pipeline && typeof pipeline === 'object' && 'pipeline_type' in pipeline) {
    return pipeline.pipeline_type as string;
  }

  return 'residential';
}

// Helper function to get both pipeline type and tenant ID from project
export async function getPipelineInfoFromProject(projectId: string): Promise<{ pipelineType: string; tenantId: string | null }> {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      pipeline_id,
      tenant_id,
      pipeline:pipelines(
        id,
        pipeline_type
      )
    `)
    .eq('id', projectId)
    .single();

  if (error) throw error;

  // Type assertion for nested relation
  const pipeline = (data as any)?.pipeline;
  const pipelineType = (pipeline && typeof pipeline === 'object' && 'pipeline_type' in pipeline)
    ? pipeline.pipeline_type as string
    : 'residential';

  return {
    pipelineType,
    tenantId: (data as any)?.tenant_id || null
  };
}

// Helper to group dispositions by group_name
export function groupDispositionsByCategory(configs: DispositionConfig[]) {
  const grouped: Record<string, DispositionConfig[]> = {};

  configs.forEach(config => {
    if (!grouped[config.group_name]) {
      grouped[config.group_name] = [];
    }
    grouped[config.group_name].push(config);
  });

  // Sort groups by group_order
  const sortedGroups = Object.entries(grouped).sort((a, b) => {
    const aOrder = a[1][0]?.group_order || 999;
    const bOrder = b[1][0]?.group_order || 999;
    return aOrder - bOrder;
  });

  return Object.fromEntries(sortedGroups);
}